import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import moment from 'moment';
import { ConversionDateTimeService } from '../../services/conversion/conversion-datetime.service';
import { DatePickerDialogComponent } from './components/date-picker-dialog.component';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
  templateUrl: './app-date-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDatePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AppDatePickerComponent),
      multi: true,
    },
  ],
})
export class AppDatePickerComponent
  implements ControlValueAccessor, OnInit, OnChanges, Validator
{
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() required = false;
  @Input() label = '';
  @Input() dateOnly = false;

  @Output() dateSelected = new EventEmitter<string>();

  control = new FormControl<string>('', []);
  private dialog = inject(MatDialog);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange?: () => void;
  private internalUpdate = false;

  constructor(private conversionDateTimeService: ConversionDateTimeService) {}

  ngOnInit() {
    if (this.required) this.control.addValidators(Validators.required);

    // 🔹 Trigger revalidation for parent whenever internal control changes
    this.control.statusChanges.subscribe(() => {
      if (this.onValidatorChange) this.onValidatorChange();
    });
  }

  ngOnChanges(_: SimpleChanges): void {
    if (this.onValidatorChange) this.onValidatorChange();
  }

  // 🔹 Angular form writes value → update internal control
  writeValue(value: string | null): void {
    if (this.internalUpdate) return;

    if (value && moment(value, moment.ISO_8601, true).isValid()) {
      const formatted = this.conversionDateTimeService.convertDate(value);
      this.control.setValue(formatted, { emitEvent: false });
    } else {
      this.control.setValue('', { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  //  Validate for required, minDate, format
  validate(_: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};
    const value = this.control.value?.trim();
    const date = this.parseInputToDate(value);

    if (this.required && !value) {
      errors['required'] = true;
    }

    if (value && !date) {
      errors['invalidFormat'] = true;
    }

    if (this.minDate && date && date < this.minDate) {
      errors['minDate'] = true;
    }

    if (this.maxDate && date && date > this.maxDate) {
      errors['maxDate'] = true;
    }

    //  Don’t set control errors manually here — just return them
    return Object.keys(errors).length ? errors : null;
  }

  // 🔹 Manual input typing
  onManualInput(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value.trim();
    const date = this.parseInputToDate(inputValue);

    if (!date) {
      this.control.setErrors({ invalidFormat: true });
      return;
    }

    if (this.minDate && date < this.minDate) {
      this.control.setErrors({ minDate: true });
      return;
    }
    if (this.maxDate && date > this.maxDate) {
      this.control.setErrors({ maxDate: true });
      return;
    }

    this.control.setErrors(null);
    const iso = this.dateOnly
  ? moment(date).format('YYYY-MM-DD')
  : date.toISOString();

    this.internalUpdate = true;
    this.onChange(iso);
    this.dateSelected.emit(iso);
    this.internalUpdate = false;
  }

  onInputBlur() {
    this.onTouched();
  }

  openDatePicker() {
    const parsedDate = this.parseInputToDate(this.control.value || '');
    const dialogRef = this.dialog.open(DatePickerDialogComponent, {
      width: '450px',
      data: {
        selectedDate: parsedDate,
        minDate: this.minDate,
        maxDate: this.maxDate,
      },
    });

    dialogRef.afterClosed().subscribe((result: Date | null) => {
      if (result) {
        if (this.minDate && result < this.minDate) {
          this.control.setErrors({ minDate: true });
          return;
        }

        const formatted =
          this.conversionDateTimeService.convertFormattedDate(result);
        const iso = this.dateOnly
        ? moment(result).format('YYYY-MM-DD')
        : result.toISOString();

        this.internalUpdate = true;
        this.control.setValue(formatted, { emitEvent: false });
        this.onChange(iso);
        this.dateSelected.emit(iso);
        this.onTouched();
        this.internalUpdate = false;
      }
    });
  }

  expectedFormatText(): string {
    return this.conversionDateTimeService.preferredDateFormat;
  }

  private parseInputToDate(value: string | null): Date | null {
    if (!value) return null;
    const fmt = this.conversionDateTimeService.preferredDateFormat;
    const m = moment(value, fmt, true);
    return m.isValid() ? m.toDate() : null;
  }
  formattedMinDate() {
    return this.conversionDateTimeService.convertDate(this.minDate);
  }

  formattedMaxDate() {
    return this.conversionDateTimeService.convertDate(this.maxDate);
  }
}
